import {
  BackSide,
  Color,
  Mesh,
  ShaderMaterial,
  SphereGeometry,
  Vector3,
  type Scene,
} from 'three';
import { PALETTE } from '@/config';

/**
 * The shan-shui sky dome.
 *
 * A single inverted sphere that rides along with the camera so the horizon never
 * moves relative to the player — the world sits inside a paper lantern. All of the
 * look lives in one fragment shader: a soft vertical wash from pale horizon paper
 * up to a slightly warmer, lighter zenith, a gentle gaussian sun (which cools into
 * a pale moon at night), one barely-there drifting cloud band near the horizon, and
 * a whisper of high-frequency paper grain so the gradient never bands.
 *
 * It is deliberately the cheapest object in the scene: no lighting, no fog, no
 * depth interaction. It draws first (very low renderOrder, depthTest off) purely as
 * a backdrop; everything else paints over it. `mood` desaturates and darkens the
 * whole dome when the city is bleak; `night` lerps the palette toward deep ink.
 */

// Direction of the scene's key light, matching the lighting rig (~normalize(60,90,40)).
const _sunDir = new Vector3(60, 90, 40).normalize();

export class Sky {
  private readonly mesh: Mesh;
  private readonly material: ShaderMaterial;

  constructor(scene: Scene) {
    // Radius 700 sits comfortably inside the camera's 800 far plane.
    const geo = new SphereGeometry(700, 32, 24);

    this.material = new ShaderMaterial({
      // A backdrop, not a surface: unlit, un-fogged, never writes/tests depth so
      // it can never occlude (or be occluded by) the world it wraps.
      side: BackSide,
      fog: false,
      depthWrite: false,
      depthTest: false,
      uniforms: {
        uTime: { value: 0 },
        uMood: { value: 1 },
        uNight: { value: 0 },
        uSunDir: { value: _sunDir.clone() },
        // Day gradient: horizon paper -> a touch warmer/lighter overhead.
        uDayHorizon: { value: new Color(PALETTE.fog) },
        uDayZenith: { value: new Color(PALETTE.sky).multiplyScalar(1.04) },
        // Night gradient.
        uNightHorizon: { value: new Color(PALETTE.nightHorizon) },
        uNightZenith: { value: new Color(PALETTE.nightZenith) },
        // Soft sun/moon disc colour.
        uSun: { value: new Color(PALETTE.sunGlow) },
      },
      vertexShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          // The dome is centred on the camera, so the un-rotated local position
          // already *is* the view direction. Keep it in local space so the
          // gradient stays pinned to world-up regardless of where we look.
          vDir = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        varying vec3 vDir;
        uniform float uTime, uMood, uNight;
        uniform vec3 uSunDir;
        uniform vec3 uDayHorizon, uDayZenith, uNightHorizon, uNightZenith, uSun;

        // --- Cheap value noise, used only for the cloud band + paper grain. ---
        float hash(vec2 p) {
          p = fract(p * vec2(123.34, 345.45));
          p += dot(p, p + 34.345);
          return fract(p.x * p.y);
        }
        float vnoise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          float a = hash(i);
          float b = hash(i + vec2(1.0, 0.0));
          float c = hash(i + vec2(0.0, 1.0));
          float d = hash(i + vec2(1.0, 1.0));
          return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
        }
        float fbm(vec2 p) {
          float s = 0.0, a = 0.5;
          for (int i = 0; i < 3; i++) { s += a * vnoise(p); p *= 2.03; a *= 0.5; }
          return s;
        }

        void main() {
          // Vertical wash. vDir.y in [-1,1]; bias the ease so most of the dome is
          // the calm zenith tone and the warmth gathers low on the horizon.
          float h = clamp(vDir.y * 0.5 + 0.5, 0.0, 1.0);
          float grad = smoothstep(0.0, 0.9, h);
          vec3 day = mix(uDayHorizon, uDayZenith, grad);
          vec3 nightCol = mix(uNightHorizon, uNightZenith, grad);
          vec3 col = mix(day, nightCol, uNight);

          // --- Sun / moon disc ------------------------------------------------
          // Angular proximity to the light; a tight core plus a wide, soft halo.
          float sd = max(dot(normalize(vDir), uSunDir), 0.0);
          float d = 1.0 - sd;
          float disc = exp(-d * 240.0);
          float halo = exp(-d * 11.0) * 0.35;
          // By day a warm sun; by night it cools to a smaller, paler moon.
          float sunI = (disc + halo) * mix(1.0, 0.55, uNight);
          col += uSun * sunI * (0.9 - 0.3 * uNight);

          // --- Cloud band near the horizon -----------------------------------
          // Sampling vDir.xz is seamless (no atan seam) and near-degenerate only
          // at the poles, which we never see. A soft mask keeps it hugging the
          // horizon, and it drifts very slowly.
          float cloud = fbm(vDir.xz * 2.6 + vec2(uTime * 0.010, uTime * 0.006));
          float band = exp(-pow((vDir.y - 0.05) / 0.11, 2.0));
          cloud = smoothstep(0.45, 0.85, cloud) * band;
          // Clouds read as a faint lightening of the paper, dimmer at night.
          vec3 cloudTint = mix(uDayZenith, uNightHorizon, uNight) * 1.05;
          col = mix(col, cloudTint, cloud * 0.14 * (1.0 - 0.4 * uNight));

          // --- Paper grain ----------------------------------------------------
          // High-frequency dither at ~2% amplitude so the smooth wash never bands.
          float grain = hash(vDir.xy * 900.0 + vDir.z * 133.0) - 0.5;
          col += grain * 0.02;

          // --- Mood: bleak cities drain the sky of saturation and value -------
          float lum = dot(col, vec3(0.299, 0.587, 0.114));
          col = mix(vec3(lum), col, mix(0.6, 1.0, uMood));
          col *= mix(0.88, 1.0, uMood);

          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });

    this.mesh = new Mesh(geo, this.material);
    this.mesh.frustumCulled = false;
    // Draw before anything else so it is a pure backdrop.
    this.mesh.renderOrder = -1000;
    scene.add(this.mesh);
  }

  update(elapsed: number, camPos: Vector3, mood: number, night: number): void {
    // Follow the camera (cheap position copy) so the horizon stays put.
    this.mesh.position.copy(camPos);
    this.material.uniforms.uTime.value = elapsed;
    this.material.uniforms.uMood.value = mood;
    this.material.uniforms.uNight.value = night;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
    this.mesh.removeFromParent();
  }
}
